import React, {useContext, useMemo, useRef, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {useNavigate, useParams} from "react-router-dom";
import {FaPrint} from "react-icons/fa";
import {toast} from "react-toastify";
import Swal from "sweetalert2";
import TemplateDefault from "../../../components/template-default";
import Card from "../../../components/card";
import Button from "../../../components/button";
import PersonalSection from "../components/personal-section";
import ProfessionalSection from "../components/professional-section";
import BenefitsSection from "../components/benefits-section";
import Observation from "../components/observation";
import Attaches from "../components/attaches";
import Qualifications from "../components/qualifications";
import CandidateSheet from "../components/candidate-sheet";
import {formatSalary, parseSalary} from "../../../utils/mask";
import {AlertMessage} from "../../../utils/alert-message";
import {cvCandidateService} from "../../../services/curriculum-resource-service";
import {ProjectContext} from "../../../context/project-context";

const sections = [
    {id: "print", label: "Resumo"},
    {id: "personal", label: "Dados pessoais"},
    {id: "professional", label: "Dados profissionais"},
    {id: "others", label: "Outras informações"}
];

const initialState = {
    name: "",
    gender: null,
    rg: "",
    cpf: "",
    cin: "",
    birth_date: null,
    age: "",
    mother_name: "",
    city: "",
    email: "",
    phone: "",
    formation: "",
    cnh: false,
    cnh_category: "",
    department_id: null,
    position_id: null,
    interview_date: null,
    feedback_date: null,
    evaluation_date: null,
    evaluation: "",
    referral: "",
    employment_regime: null,
    employed: false,
    last_employer: "",
    last_salary: "",
    salary_expectation: "",
    can_relocate: false,
    attachments: [],
    benefits: [],
    observation: "",
    qualifications: []
};

const CvCandidateForm = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const {user} = useContext(ProjectContext);
    const isViewOnly = !user.permissions.includes("cv_candidates")
        && user.permissions.includes("cv_candidates_view_only");
    const [activeSection, setActiveSection] = useState("personal");
    const [state, setState] = useState(initialState);
    const [benefitsTotal, setBenefitsTotal] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const attachmentsRef = useRef();
    const benefitsRef = useRef();
    const totalRevenue = useMemo(() => (
        formatSalary(parseSalary(state.last_salary) + benefitsTotal)
    ), [benefitsTotal, state.last_salary]);

    const {isFetching, refetch} = useQuery({
        queryFn: () => cvCandidateService.getById(id).then(({data}) => {
            const candidate = {
                ...initialState,
                ...data,
                department_id: data.department ? {
                    value: data.department.id,
                    label: data.department.name,
                } : null,
                position_id: data.position ? {
                    value: data.position.id,
                    label: data.position.name,
                } : null,
                birth_date: parseDate(data.birth_date),
                interview_date: parseDate(data.interview_date),
                feedback_date: parseDate(data.feedback_date),
                evaluation_date: parseDate(data.evaluation_date),
                last_salary: data.last_salary ? formatSalary(data.last_salary) : "",
                salary_expectation: data.salary_expectation ? formatSalary(data.salary_expectation) : "",
                attachments: data.attachments ?? [],
                benefits: data.benefits ?? [],
                qualifications: data.qualifications ?? [],
            };

            setState(candidate);
            return candidate;
        }),
        queryKey: ["cv-candidate", {id}],
        enabled: !!id,
        retry: 0,
    });

    const handleInput = ({target: {id: field, value}}) => {
        setState(prev => ({...prev, [field]: value}));
    };

    const handleSelect = (field, value) => {
        setState(prev => ({...prev, [field]: value}));
    };

    const handleDateInput = (value, field) => {
        setState(prev => ({...prev, [field]: value}));
    };

    const handleCheckBox = ({target: {id: field, checked}}) => {
        setState(prev => ({...prev, [field]: checked}));
    };

    const showError = (error, fallback) => {
        const message = error.response?.data?.message;
        toast.error(AlertMessage[message] ?? fallback);
    };

    const submit = () => {
        if (isViewOnly) return;

        if (!hasValue(state.name) || !hasValue(state.department_id) || !hasValue(state.position_id)) {
            toast.error(AlertMessage.generalEmpty);
            return;
        }

        const formData = new FormData();
        const excludedFields = ["attachments", "benefits", "qualifications"];

        Object.entries(state)
            .filter(([field]) => !excludedFields.includes(field))
            .forEach(([field, value]) => {
                if (value instanceof Date) {
                    formData.append(field, formatDate(value));
                    return;
                }

                if (value && typeof value === "object") {
                    formData.append(field, value.value ?? "");
                    return;
                }

                formData.append(field, value ?? "");
            });

        const benefits = [];
        benefitsRef.current?.api.forEachNode(({data}) => benefits.push(data));

        const attachments = [];
        attachmentsRef.current?.api.forEachNode(({data}) => {
            const {file, ...metadata} = data;
            attachments.push(metadata);
            if (file) formData.append("attaches[]", file);
        });

        formData.append("benefits", JSON.stringify(benefits));
        formData.append("attachments", JSON.stringify(attachments));
        formData.append("qualifications", JSON.stringify(state.qualifications));

        if (id) formData.append("_method", "PUT");

        setIsSaving(true);
        const request = id
            ? cvCandidateService.edit(formData, id)
            : cvCandidateService.create(formData);

        request
            .then(({data}) => {
                toast.success("Salvo com sucesso.");
                id ? refetch() : navigate(`/cv/candidates/form/${data.id}`);
            })
            .catch((error) => showError(error, "Não foi possível salvar"))
            .finally(() => setIsSaving(false));
    };

    const remove = () => {
        if (isViewOnly) return;

        Swal.fire({
            icon: "warning",
            title: "Deseja realmente excluir o candidato?",
            showCancelButton: true,
            text: "Essa ação não poderá ser desfeita.",
            confirmButtonText: "Sim",
            cancelButtonText: "Não",
            confirmButtonColor: "#e64942",
            reverseButtons: true,
        }).then(({isConfirmed}) => {
            if (!isConfirmed) return;

            setIsSaving(true);
            cvCandidateService.delete(id)
                .then(() => navigate("/cv/candidates"))
                .catch((error) => showError(error, "Não foi possível excluir"))
                .finally(() => setIsSaving(false));
        });
    };

    const printCandidateSheet = () => {
        document.body.classList.add("candidate-sheet-page-print");

        try {
            window.print();
        } finally {
            document.body.classList.remove("candidate-sheet-page-print");
        }
    };

    const sectionClass = (section) => [
        "flex justify-left h-[35px] items-center focus:ring-2 focus:outline-none font-medium",
        "rounded text-sm px-4 py-2 text-center disabled:opacity-60 cursor-pointer",
        "hover:bg-gray-200 disabled:hover::bg-white",
        activeSection === section ? "bg-gray-200" : "",
    ].join(" ");

    return (
        <TemplateDefault title={`Cadastro de candidato ${id ? ` #${id}` : ""}`}
                         isLoading={isFetching || isSaving}>
            <div className="flex w-full overflow-hidden h-[calc(100vh-71px)]">
                <div className="shrink-0 h-full">
                    <Card wrapperClass="h-full flex flex-col" bodyClass="flex-1 overflow-y-auto">
                        {sections.map(({id: section, label}) => (
                            <p key={section} onClick={() => setActiveSection(section)}
                                    className={sectionClass(section)}>
                                {label}
                            </p>
                        ))}
                    </Card>
                </div>

                <div className="flex-1 h-full overflow-auto pl-4">
                    <div className="w-full h-full overflow-y-auto pr-2">
                        {activeSection === "personal" && (
                            <PersonalSection
                                state={state}
                                handleInput={handleInput}
                                handleSelect={handleSelect}
                                handleDateInput={handleDateInput}
                                handleCheckBox={handleCheckBox}
                                disabled={isViewOnly}
                            />
                        )}

                        {activeSection === "professional" && (
                            <ProfessionalSection
                                state={state}
                                handleInput={handleInput}
                                handleSelect={handleSelect}
                                handleDateInput={handleDateInput}
                                handleCheckBox={handleCheckBox}
                                disabled={isViewOnly}
                            />
                        )}

                        {activeSection === "others" && (
                            <>
                                <Card title="Capacitações">
                                    <Qualifications state={state} handleSelect={handleSelect}
                                                    disabled={isViewOnly}/>
                                </Card>

                                <Card title="Benefícios e remuneração" wrapperClass="mt-4">
                                    <BenefitsSection benefits={state.benefits} gridRef={benefitsRef}
                                                     onBenefitsChange={(benefits) => setState(prev => ({...prev, benefits}))}
                                                     onTotalChange={setBenefitsTotal}
                                                     totalRevenue={totalRevenue}
                                                     disabled={isViewOnly}/>
                                </Card>
                                <Card title="Observações" wrapperClass="mt-4">
                                    <Observation
                                        value={state.observation}
                                        onChange={(observation) => setState(prev => ({...prev, observation}))}
                                        setIsLoading={setIsSaving}
                                        disabled={isViewOnly}
                                    />
                                </Card>

                                <Card title="Anexos" wrapperClass="mt-4">
                                    <Attaches state={state} attachmentsRef={attachmentsRef}
                                              disabled={isViewOnly}/>
                                </Card>
                            </>
                        )}

                        {activeSection === "print" && (
                            <>
                                <div className="flex justify-end mb-3 print:hidden">
                                    <Button onClick={printCandidateSheet} target="primary">
                                        <FaPrint/> Imprimir
                                    </Button>
                                </div>
                                <CandidateSheet candidate={state} candidateId={id}/>
                            </>
                        )}

                        { activeSection !== "print" &&
                            <>
                                <Card wrapperClass="mt-5 mb-5">
                                    <div className="flex justify-end gap-3">
                                        {id && !isViewOnly && <Button onClick={remove} target="danger">Excluir</Button>}
                                        <Button onClick={() => navigate("/cv/candidates")} target="warning">
                                            {isViewOnly ? "Voltar" : "Cancelar"}
                                        </Button>
                                        {!isViewOnly && <Button onClick={submit} target="success">Salvar</Button>}
                                    </div>
                                </Card>
                            </>
                        }

                    </div>
                </div>
            </div>
        </TemplateDefault>
    );
};

const parseDate = (value) => value ? new Date(`${value}T00:00:00`) : null;

const hasValue = (value) => typeof value === "string" ? !!value.trim() : !!value?.value;

const formatDate = (value) => {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

export default CvCandidateForm;
